import { ContextualHelp, FieldHelpProvider, HelpTerm, TermHelpProvider } from '@ddone/ui';
import type { ReactNode } from 'react';
import {
  fieldHelpFor,
  moduleHelpForPath,
  termHelpFor,
  type HelpSection
} from './module-help-catalog';

interface GuideTopic {
  title: string;
  description: ReactNode;
  search: string;
}

export function RouteGuideHelp({ pathname }: { pathname: string }) {
  const topic = moduleHelpForPath(pathname);
  return (
    <GuideHelp
      title={topic.title}
      description={
        <div className="dd-module-help">
          <p className="dd-module-help-summary">{topic.summary}</p>
          {topic.sections.map((section) => (
            <HelpSectionList key={section.title} section={section} topicId={topic.id} />
          ))}
        </div>
      }
      search={topic.search}
      ariaLabel={`Guida completa del modulo ${topic.title.split(':')[0]}`}
      maxWidth={540}
    />
  );
}

function HelpSectionList({ section, topicId }: { section: HelpSection; topicId: string }) {
  return (
    <section className="dd-module-help-section">
      <h3>
        {section.title.toLocaleLowerCase('it').startsWith('termin')
          ? `Glossario · ${section.title}`
          : section.title}
      </h3>
      <dl>
        {section.items.map((item) => (
          <div key={item.label}>
            <dt>
              <a
                href={`/help?q=${encodeURIComponent(`${topicId} ${item.label}`)}`}
                title={`Apri la Guida: ${item.label}`}
              >
                {item.label}
              </a>
            </dt>
            <dd>{item.description}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

export function ModuleFieldHelpProvider({
  pathname,
  children
}: {
  pathname: string;
  children: ReactNode;
}) {
  const topic = moduleHelpForPath(pathname);
  const glossary = topic.sections
    .flatMap((section) => section.items.map((item) => item.label))
    .slice(0, 7);
  return (
    <TermHelpProvider
      glossary={glossary}
      resolve={(term) => {
        const help = termHelpFor(term, pathname);
        return <GuideHelp {...help} ariaLabel={`Spiegazione del termine ${term}`} maxWidth={420} />;
      }}
    >
      <FieldHelpProvider
        resolve={(fieldId, label) => {
          const help = fieldHelpFor(fieldId, label, pathname);
          return (
            <GuideHelp {...help} ariaLabel={`Spiegazione del campo ${label}`} maxWidth={420} />
          );
        }}
      >
        {children}
      </FieldHelpProvider>
    </TermHelpProvider>
  );
}

export function ModuleTermHelp({
  term,
  children,
  className
}: {
  term: string;
  children?: ReactNode;
  className?: string;
}) {
  return (
    <HelpTerm term={term} className={className}>
      {children}
    </HelpTerm>
  );
}

export function GuideHelp({
  title,
  description,
  search,
  ariaLabel,
  maxWidth
}: GuideTopic & { ariaLabel?: string; maxWidth?: number }) {
  return (
    <ContextualHelp
      title={title}
      description={description}
      ariaLabel={ariaLabel}
      maxWidth={maxWidth}
      action={{
        label: 'Approfondisci nella Guida',
        href: `/help?q=${encodeURIComponent(search)}`
      }}
    />
  );
}
